(ns ui.view.survey.components
    (:require
      [clojure.edn :as edn]
      [clojure.string :as string]
      [common.languages :as languages]
      [common.localization :as loc]
      [common.schema.data-types :as data-types]
      [common.time :as c-time]
      [common.util :as cu]
      [frontend.utils :as fu]
      [frontend.view.components.upload :as upload]
      [oops.core :as oops]
      [re-frame.core :as rf]
      [reagent.core :as r]
      [ui.components.button.view :as button]
      [ui.components.ck-editor.view :as ck-editor]
      [ui.components.grid.view :as grid]
      [ui.components.radix-ui.popover.view :as popover]
      [ui.components.radix-ui.tooltip.view :as tooltip]
      [ui.dom-globals :as dg]
      [ui.editors.data-input :as data-input]
      [ui.editors.labelled-input.view :as labelled-input]
      [ui.view.survey.event :as event]
      [ui.view.survey.sub :as sub]))

(defn new-renderer
  [number-format questions-lookup params]
  (let [{:keys [data colDef]} params
        series-index (get colDef :series-index)
        value-meta (get-in data [:series-data series-index])
        value (get value-meta :value)
        {:keys [visible? show-mandatory? dom-id lang]} value-meta
        lang (keyword lang)
        question-id (:question-id data)
        question (get questions-lookup question-id)
        data-type (:survey-question/data-type question)
        numeric? (some #{data-type} data-types/numeric-data-types)]
    (r/as-element
      (if-not visible?
        [:div
         {:class (fu/cs "flex flex-row w-full h-full bg-disabled-light text-disabled-regular-text align-items-center justify-center")}
         "N/A"]
        [:div
         {:id    dom-id
          :class (fu/cs "w-full h-full flex flex-row align-items-center"
                        (when show-mandatory?
                          "bg-danger-light hover:bg-danger-regular")
                        (if numeric?
                          "justify-content-end"
                          "justify-content-start"))}
         ; (pr-str (:survey-question/data-type question) " - " value " - " (type value) " - ")
         [data-input/formatted-display-string
          {:data-type     (:survey-question/data-type question)
           :value         value
           :number-format number-format
           :options       (:localized-options question)
           :lang          lang}]]))))

(defn section-cell-renderer
  [question question-sections lang]
  (let [localized (get-in question-sections [(:survey-question-id question) :label])]
    [:div
     {:class "overflow-visible rotate-90"}
     (or (get localized lang)
         (get localized :ENG))]))

(defn reporting-period-text
  "Nothing at all when the survey has no reporting period.

   The dates are nil in that case, and the label with an empty range after it
   told the respondent less than showing no line does."
  [survey-id lang]
  (let [reference-start-date @(rf/subscribe [::sub/reference-start-date survey-id])
        reference-end-date @(rf/subscribe [::sub/reference-end-date survey-id])]
    (when (and reference-start-date reference-end-date)
      [:div
       {:class-name "flex items-center gap-2"}
       [:span
        {:class-name "m-0 text-bold text-complement-light-text-paler text-sm"}
        (str (loc/tr :survey-reporting-period nil lang) ":")]
       [:span
        {:class-name "m-0 inline-block text-complement-light-text-paler rounded-full text-sm"}
        (str reference-start-date " - " reference-end-date)]])))

(defn lang-selector
  [survey-id lang]
  (let [supported-languages @(rf/subscribe [::sub/supported-languages survey-id])]
    (when (> (count supported-languages) 1)
      [labelled-input/view
       {:editor-type :editor.type/select
        :placeholder (loc/tr :select nil lang)
        :label       (loc/tr :language nil lang)
        :on-change   (fn [v]
                       (rf/dispatch-sync [::event/change-language survey-id (edn/read-string v)]))
        :value       {:value lang
                      :label (languages/get-local-label lang)}
        :options     (->> supported-languages
                          (map (fn [lang-kw]
                                 {:value (pr-str lang-kw)
                                  :label (languages/get-local-label lang-kw)}))
                          (doall))
        :class       "min-w-20"}])))

(defn thousand-separator
  []
  (let [lang @(rf/subscribe [::sub/selected-lang])
        separator @(rf/subscribe [::sub/thousand-separator])
        options @(rf/subscribe [::sub/thousand-separator-options])]
    [labelled-input/view
     {:label       (loc/tr :thousand-separator nil lang)
      :editor-type :editor.type/select
      :options     options
      :value       (->> options
                        (cu/filter-by :value separator)
                        (first))
      :on-change   (fn [s]
                     (rf/dispatch-sync [::event/set-thousand-separator s]))
      :class       "min-w-24"}]))

(defn decimal-separator
  []
  (let [lang @(rf/subscribe [::sub/selected-lang])
        separator @(rf/subscribe [::sub/decimal-separator])
        options @(rf/subscribe [::sub/decimal-separator-options])]
    [labelled-input/view
     {:label       (loc/tr :decimal-separator nil lang)
      :editor-type :editor.type/select
      :options     options
      :value       (->> options
                        (cu/filter-by :value separator)
                        (first))
      :on-change   (fn [s]
                     (rf/dispatch-sync [::event/set-decimal-separator s]))
      :class       "min-w-24"}]))

(defn add-remove-columns-buttons
  [full? survey-id lang]
  (let [series-count @(rf/subscribe [::sub/series-count survey-id])
        unsaved-changes @(rf/subscribe [::sub/unsaved-survey-answers survey-id])]
    [:<>
     (when (> series-count 1)
       (if unsaved-changes
         [tooltip/view
          [:span
           {:class button/tooltip-disabled-open-classes}
           [:span
            [:span [:i.fal.fa-trash-alt]]
            (loc/tr :remove-last-column nil lang)]]
          [:div
           (loc/tr :save-survey-before-removing-columns nil lang)]]
         [button/secondary-open
          {:on-click (fn [_]
                       (rf/dispatch [::event/remove-last-answer-series survey-id]))
           :tooltip  (when-not full?
                       (loc/tr :remove-last-column nil lang))}
          [:span [:i.fal.fa-trash-alt]]
          (when full?
            (loc/tr :remove-last-column nil lang))]))
     [button/secondary-open
      {:on-click (fn [_]
                   (rf/dispatch [::event/add-answer-series survey-id]))
       :tooltip  (when-not full?
                   (loc/tr :add-column nil lang))}
      [:span [:i.fal.fa-plus]]
      (when full?
        (loc/tr :add-column nil lang))]]))

(defn cell-edit-request
  [survey-id number-format _questions-lookup _lang params-js]
  (let [{:keys [_rowIndex _value _api data _source _node _column _event _context newValue colDef]} (js->clj params-js :keywordize-keys true)
        {:keys [data-type option-value-lookup id]} data
        data-type (edn/read-string data-type)
        series-index (:series-index colDef)
        field-key (keyword (:field colDef))
        current-value (get data field-key)
        d-separator @(rf/subscribe [::sub/decimal-separator])
        t-separator @(rf/subscribe [::sub/thousand-separator])
        formattedValue (if-not (some #{data-type} [:data.type/currency-value :data.type/number])
                         newValue
                         (-> newValue
                             (str)
                             (string/replace (str t-separator) "")
                             (string/replace (str d-separator) ".")))
        parsed-value (if (data-types/numeric-data-types data-type)
                       (cond (number? formattedValue)
                             formattedValue
                             (string? formattedValue)
                             (edn/read-string formattedValue)
                             :else
                             formattedValue)
                       (data-input/parse-value
                         {:data-type           (data-types/to-keyword-data-type data-type)
                          :native-value        formattedValue
                          :option-value-lookup option-value-lookup
                          :date-format         nil
                          :number-format       number-format}))
        clearedValue (cond (and (some #{data-type} [:data.type/currency-value :data.type/integer :data.type/number])
                                (js/isNaN parsed-value))
                           nil
                           (and (some #{data-type} [:data.type/ref-single])
                                (string? parsed-value))
                           (edn/read-string parsed-value)
                           (and (some #{data-type} [:data.type/ref-multi])
                                (some string? parsed-value))
                           (->> parsed-value
                                (mapv edn/read-string))
                           :else
                           parsed-value)]
    (when-not (and (nil? newValue) (some? current-value))
       (rf/dispatch [::event/on-answer-input survey-id series-index id clearedValue false nil nil]))
     true))

(def editor-cell-atom (atom {}))

(defn edit-renderer2
  [number-format questions-lookup lang params-js]
  (let [{:keys [value onValueChange data column api rowIndex] :as params} (js->clj params-js :keywordize-keys true)
        editor-width (aget column "actualWidth")
        col-id (aget column "colId")
        series-index (get-in params [:colDef :series-index])
        value-meta (get-in data [:series-data series-index])
        param-question-id (get-in data [:question-id])
        question (get questions-lookup param-question-id)
        {:keys [survey-question/data-type survey-question/options]} question
        option-type? (some #{data-type} [:data.type/keyword :data.type/ref-single :data.type/ref-multi :data.type/boolean
                                         :data.type/keyword-multi])
        single-type? (some #{data-type} [:data.type/keyword :data.type/ref-single :data.type/boolean])
        base-options (if (= data-type :data.type/boolean)
                       [{:value true
                                     :label (loc/tr :yes nil lang)}
                        {:value false
                                     :label (loc/tr :no nil lang)}]
                                   (->> options
                                        (sort-by :common/ord)
                                        (map (fn [opt]
                                               (let [label (:label opt)]
                                                 (if (string? label)
                                                   opt
                                                   (assoc opt :label (cond (map? label)
                                                                           (or (get label lang)
                                                                   (get label :ENG))))))))))
        clear-option {:value nil
                      :label (loc/tr :clear-select-option-label nil lang)}
        resolved-options (if (cu/blank-value? value)
                           base-options
                           (if (= data-type :data.type/boolean)
                             (conj (vec base-options) clear-option)
                             (concat [clear-option] base-options)))
        cell-value (if (some? value)
                     value
                     (:value value-meta))
        resolved-value (cond (= data-type :data.type/boolean)
                             (let [v (if (string? cell-value)
                                       (edn/read-string cell-value)
                                       cell-value)]
                             (->> resolved-options
                                  (filter (fn [opt]
                                              (= v (:value opt))))
                                    (first)))
                             (and option-type? single-type?)
                             (->> resolved-options
                                  (filter (fn [opt]
                                            (some #{cell-value} [(:value opt) (pr-str (:value opt))])))
                                  (first))
                             option-type?
                             (->> resolved-options
                                  (filter (fn [opt]
                                            (some #{(:value opt) (pr-str (:value opt))} cell-value))))
                             (= data-type :data.type/date)
                             (if (string? cell-value)
                               (edn/read-string cell-value)
                               cell-value)
                             :else
                             cell-value)]
    (reset! editor-cell-atom {:row-index rowIndex
                              :col-id    col-id})
    (r/as-element
      (if-not (:visible? value-meta)
        [:div
         {:class (fu/cs "flex flex-row w-full h-full bg-disabled-light text-disabled-regular-text align-items-center justify-center")}
         "N/A"]
        [:div
         {:on-key-down (fn [event]
                         (let [event-key (oops/oget event "key")
                               enter? (= event-key "Enter")]
                           (if enter?
                             (swap! editor-cell-atom assoc :enter-while-editing? true)
                             (swap! editor-cell-atom assoc :enter-while-editing? false))))
          :style       {:width      (str editor-width "px")
                        :background "white"
                        :height     "100%"}}
         [data-input/view
          {:editor-type   (case data-type
                            :data.type/boolean :editor.type/select
                            nil)
           :data-type     (case data-type
                            :data.type/boolean :data.type/keyword
                            data-type)
           :number-format number-format
           :class         "w-full h-full"
           :variant       :borderless
           :small?        true
           :full-width?   true
           :auto-focus?   true
           :show-clear?   true
           :placeholder   (if (some #{data-type} [:data.type/ref-single :data.type/ref-multi :data.type/keyword :data.type/keyword-multi])
                            (loc/tr :select-placeholder nil lang)
                            (loc/tr data-type nil lang))
           :value         resolved-value
           :options       (->> resolved-options
                               (map (fn [opt]
                                     (if (= data-type :data.type/boolean)
                                       opt
                                       (assoc opt :value (pr-str (:value opt)))))))
           :on-change     (fn [new-value]
                           (let [new-value (if (and (= :data.type/boolean data-type)
                                                    (string? new-value))
                                              (edn/read-string new-value)
                                              new-value)]
                              (try (onValueChange new-value)
                                   (when (and option-type?
                                              resolved-options)
                                     (js/console.log "Stop editing : " resolved-options)
                                     (oops/ocall api "stopEditing" false)
                                     (oops/ocall api "setFocusedCell" rowIndex col-id))
                                   (catch js/Object o
                                     (js/console.log "onValuechange error : " o)))))
           :on-blur       (fn [new-value]
                            (when (or new-value
                                      (boolean? new-value))
                             (let [new-value (if (and (= :data.type/boolean data-type)
                                                      (string? new-value))
                                                (edn/read-string new-value)
                                                new-value)]
                                (try
                                  (onValueChange new-value)
                                  (catch js/Object o
                                    (js/console.log "onValuechange error : " o))))))}]]))))

(defn info-icon
  [{:keys [_survey-id _survey-question-id _info _label show-question-ext-ids? question-ext-id-labels]}]
  (let [state (r/atom {})]
    (fn [{:keys [info label]}]
      [popover/view
       {:open?          (:open? @state)
        :on-open-change (fn [_]
                          (swap! state update :open? not))}
       [:i
        {:class    "fas fa-info-circle text-base-light-text-paler hover:text-base-light-text-accent"
         :on-click (fn [_e])}]

       [:div
        {:class "flex flex-col"}
        [:div
         {:class "flex-1 flex flex-row justify-end max-w-[80vw] sm:max-w-[400px] md:max-w-[600px] lg:max-w-[800px]"}
         [ck-editor/viewer
          (str "<p><strong>" (string/upper-case label) "</strong></p>"
               (if (string/starts-with? info "<")
                 info
                 (str "<p>" info "</p>")))]
         [:i
          {:class    "fas fa-times-circle"
           :on-click (fn [_]
                       (swap! state assoc :open? false))}]]
        (when show-question-ext-ids?
          [:div
           {:class "text-xs text-complement-regular-text font-thin"}
           question-ext-id-labels])]])))

(defn label-cell-renderer
  [question survey-id show-question-ext-ids? lang]
  (let [label (get-in question [:survey-question/label-translations lang])
        question-ext-id-labels @(rf/subscribe [::sub/survey-question-ext-id-labels survey-id question])]
    [:div
     {:class "w-full h-full flex justify-between items-center gap-2"}
     [:div
      {:class "w-4 shrink-0 grow-o flex"}
      (when-let [info (get-in question [:survey-question/info-translations lang])]
        (when-not (cu/blank-value? info)
          [info-icon {:label                   label
                      :info                    info
                      :show-question-ext-ids?  show-question-ext-ids?
                      :question-ext-id-labels question-ext-id-labels}]
          #_[tooltip/view
             {:side :right}
             [:i.fal.fa-info-circle.text-complement-regular-text-paler]
             [:div
              {:class "bg-base-gloomy max-w-64"}
              [ck-editor/viewer
               (str "<p><strong>" (string/upper-case label) "</strong></p>"
                    (if (string/starts-with? info "<")
                      info
                      (str "<p>" info "</p>")))]
              (when show-question-ext-ids?
                [:div
                 {:class "text-xs text-complement-regular-text font-thin"}
                 question-ext-id-labels])]]))]

     [:div
      {:class "flex-auto line-clamp-2 leading-relaxed py-2"}
      (str label)]
     [:div
      {:class "h-full"}
      (when-let [data-unit (:survey-question/data-unit question)]
        [:span
         {:class "p-2 h-full flex items-center items-center gap-2 text-xs text-complement-regular-text font-normal whitespace-nowrap border-l border-solid border-complement-regular-border"}
         data-unit])]]))

(defn grid-suppress-key
  [questions-lookup params-js]
  (let [{:keys [event data]} (js->clj params-js :keywordize-keys true)
        {:keys [question-id]} data
        event-key (oops/oget event "key")
        enter? (= event-key "Enter")
        question (get questions-lookup question-id)
        data-type (get-in question [:survey-question/data-type])
        suppress-enter-type?
        (some #{data-type} [:data.type/keyword :data.type/ref-single :data.type/ref-multi :data.type/boolean
                            :data.type/keyword-multi :data.type/text-multiline])]
    (and enter? suppress-enter-type?)))

(defn section-col-def
  [questions question-sections]
  {:field      "section"
   :headerName ""
   :width      40
   :pinned     "left"
   :resizable  false
   :class      "my-custom-class"                            ;; Added class key
   :cellClass  (fn [params-js]
                 (let [rowIndex (oops/oget params-js "node.rowIndex")
                       question (nth questions rowIndex)
                       row-color (get-in question-sections [(:survey-question-id question) :color])]
                   (str "!justify-start !pl-0 " row-color)))
   :rowSpan    (fn [params-js]
                 (let [rowIndex (oops/oget params-js "node.rowIndex")
                       question (nth questions rowIndex)
                       row-span (get-in question-sections [(:survey-question-id question) :span])]

                   (or row-span 0)))})

(defn question-col-def
  [question-min-width doc-width]
  {:field        "question"
   :headerName   ""
   :pinned       "left"
   :wrapText     true
   :resizable    true
   :autoHeight   true
   :cellClass    "font-semibold"
   :minWidth     question-min-width
   :initialWidth (if (< doc-width 700)
                   (- doc-width 100)
                   650)})

(defn response-col-def
  [{:keys [lang col-series-min-width series-index]}]
  {:series-index series-index
   :field        (str "resp-" series-index)
   :headerName   (loc/tr :survey-response-label [(inc series-index)] lang)
   :minWidth     col-series-min-width
   :initialWidth 240
   :cellDataType false})

(defn next-cell-focus
  [& params-js]
  (let [{:keys [backwards api previousCellPosition nextCellPosition]}
        (js->clj params-js :keywordize-keys true)
        last-row-index (oops/ocall api "getLastDisplayedRowIndex")
        columns (oops/ocall api "getColumns")
        last-column (last columns)
        first-column (->> columns
                          (filter (fn [column]
                                    (string/starts-with? (str (oops/oget column "colId")) "resp-")))
                          (first))
        previous-row-index (:rowIndex previousCellPosition)
        next-row-index (:rowIndex nextCellPosition)
        next-cell-column (:column nextCellPosition)
        res (cond
              (nil? previous-row-index)
              {:rowIndex  0
                   :rowPinned nil
               :column    first-column}

              (nil? next-cell-column)
                  {:rowIndex  -1
                   :rowPinned nil
               :column    nil}

              :else
              (let [next-cell-colId (oops/oget next-cell-column "colId")
                    resp-col? (string/starts-with? (str next-cell-colId) "resp-")
                    edit-next (if resp-col?
                                nextCellPosition
                                (if backwards
                                  (if (zero? next-row-index)
                                    {:rowIndex  -1
                                     :column    nil
                                     :rowPinned nil}
                                    {:rowIndex  (dec next-row-index)
                                     :column    last-column
                                     :rowPinned nil})
                                  (if (not= next-row-index previous-row-index)
                                    {:rowIndex  next-row-index
                                     :column    first-column
                                     :rowPinned nil}
                                    {:rowIndex  last-row-index
                                     :column    last-column
                                     :rowPinned nil})))]
                    edit-next))]
    (clj->js res)))

(defn next-header-focus
  [& _params-js]
  (clj->js {:headerRowIndex -1
            :column         nil}))

(defn ag-grid
  [{:keys [survey-id question-min-width col-series-min-width]}]
  (let [lang @(rf/subscribe [::sub/selected-lang survey-id])
        number-format @(rf/subscribe [::sub/num-fmt])
        grid-data @(rf/subscribe [::sub/grid-data survey-id])
        series-count @(rf/subscribe [::sub/series-count survey-id])
        doc-width (oops/oget js/document "body.clientWidth")
        [questions question-sections] @(rf/subscribe [::sub/survey-questions survey-id])
        questions-lookup (->> questions
                              (map (fn [q]
                                     [(:survey-question-id q) q]))
                              (into {}))
        show-question-ext-ids? @(rf/subscribe [::sub/show-questions-ext-ids? survey-id])
        column-defs (vec (concat [(section-col-def questions question-sections)
                                  (question-col-def question-min-width doc-width)]
                                 (->> (range 0 series-count)
                                      (map (fn [series-index]
                                             (response-col-def {:survey-id            survey-id
                                                                :number-format        number-format
                                                                :lang                 lang
                                                                :questions-lookup     questions-lookup
                                                                :col-series-min-width col-series-min-width
                                                                :series-index         series-index}))))))]
    [grid/ag-grid
     {:theme                         grid/theme-quartz
      :suppressRowTransform          true
      :rowBuffer                     (count grid-data)      ;; Keep all rows in buffer
      :pinnedTopRowData              []
      :getRowId                      (fn [data-js]
                                       (str (oops/oget data-js "data.id")))
      :suppressContextMenu           true
      :stopEditingWhenCellsLoseFocus true
      :columnDefs                    column-defs
      :rowData                       grid-data
      :domLayout                     "normal"
      :readOnlyEdit                  true
      :onCellEditRequest             (partial cell-edit-request survey-id number-format questions-lookup lang)
      :tabToNextCell                 next-cell-focus
      :tabToNextHeader               next-header-focus      ;"autoHeight"
      :onCellKeyDown                 (fn [params-js]
                                       (let [{:keys [api event column rowIndex]} (js->clj params-js :keywordize-keys true)
                                             e-key (oops/oget event "key")
                                             col-id (oops/oget column "colId")
                                             focusedCell (oops/ocall api "getFocusedCell")
                                             focusedColumn (aget focusedCell "column")
                                             focused-row-index (aget focusedCell "rowIndex")
                                             focused-col-id (aget focusedColumn "colId")
                                             editing? (and (= focused-row-index (:row-index @editor-cell-atom))
                                                           (= focused-col-id (:col-id @editor-cell-atom)))
                                             since-edit (- (c-time/now-millis) (or (:ts @editor-cell-atom) 0))]
                                         (when (and (not editing?)
                                                    (> since-edit 50)
                                                    (or (= e-key "Enter") (= e-key " ")))
                                           #_(js/console.log "NODE : " node
                                                             "\n\n > " editing? " << " enter-while-editing?
                                                             "\n" editors " > " (js->clj editors :keywordize-keys true)
                                                             "\n" (js->clj (.. api -getEditingCells) :keywordize-keys true)
                                                             "\n" {:row-index focused-row-index
                                                                   :col-id    focused-col-id}
                                                             "\n" @editor-cell-atom)
                                           ;; Start editing
                                           (oops/ocall api "startEditingCell"
                                                       #js {:rowIndex rowIndex
                                                            :colKey   col-id})

                                           ;; Prevent page scroll when pressing space
                                           (when (= e-key " ")
                                             (dg/prevent-default event)))))
      :onCellEditingStopped          (fn [e]
                                       (swap! editor-cell-atom assoc
                                              :row-index nil
                                              :col-index nil
                                              :ts (c-time/now-millis)))
      :defaultColDef                 {:sortable                 false
                                      :filter                   false
                                      :cellClass                "absolute flex-col"
                                      :resizable                true
                                      :singleClickEdit          true
                                      :suppressKeyboardEvent    (fn [params-js]
                                                                  (grid-suppress-key questions-lookup params-js))
                                      :cellEditor               (partial edit-renderer2 number-format questions-lookup lang)
                                      :editable                 (fn [params-js]
                                                                  (let [params (js->clj params-js :keywordize-keys true)
                                                                        {:keys [column data]} params
                                                                        col-id (oops/oget column "colId")
                                                                        series-index (get-in params [:colDef :series-index])
                                                                        visible? (get-in data [:series-data series-index :visible?])]
                                                                    (and (not (some #{"section" "question"} [col-id]))
                                                                         visible?)))
                                      :suppressHeaderMenuButton true
                                      :suppressMovable          true
                                      :cellRenderer             (fn [params-js]
                                                                  (let [params (js->clj params-js :keywordize-keys true)
                                                                        {:keys [column data]} params
                                                                        question-index (:question-index data)
                                                                        column-id (oops/oget column "colId")
                                                                        question (nth questions question-index)]
                                                                    (r/as-element
                                                                      (case column-id
                                                                        "question" [label-cell-renderer question survey-id show-question-ext-ids? lang]
                                                                        "section" [section-cell-renderer question question-sections lang]
                                                                        [new-renderer number-format questions-lookup params]))))}}]))

#_(defn excel-block
  [survey-id]
  (let [lang @(rf/subscribe [::sub/selected-lang survey-id])
        show-excel? @(rf/subscribe [::sub/excel-visible? survey-id])]
    (when show-excel?
      [:div
       {:class "hidden md:grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 pb-6"}
       [:div
        {:class "flex justify-center items-center text-complement-regular-text-paler border border-dashed border-complement-regular-border-paler hover:border-complement-regular-border rounded"}
        [:button
         {:on-click #(rf/dispatch [::event/generate-excel survey-id])
          :class    "p-2 md:p-0 flex flex-col justify-center items-center"}
         [:i
          {:class "fas fa-file-download mb-3 text-3xl text-complement-regular-text-paler"}]
         [:span
          {:class "text-complement-regular-text"}
          (loc/tr :you-can-fill-the-survey-in-exel nil lang)]
         [:span
          {:class "text-complement-regular-text-accent text-bold"}
          (loc/tr :download-excel-template nil lang)]]]
       [:div
        {:class "flex flex-1"}
        [upload/upload-zone
         {:css-class  "attachments-module-upload-zone mb-0"
          :accept     ".xlsx, .xls"
          :lang-kw    (languages/long->short lang)
          :on-change! (fn [e]
                        (let [file (aget (:files e) 0)]
                          (rf/dispatch [::event/import-excel survey-id file])))}]]])))

(defn tesi-company-excel-block
  [survey-id]
  (let [show-excel? @(rf/subscribe [::sub/excel-visible? survey-id])]
    (when show-excel?
      [:div
       {:class "flex items-center flex-auto justify-end gap-2"}
       [:div
        {:class "h-10 pt-3 flex flex-row content-stretch gap-4"}
        [button/primary-solid
         {:on-click (fn []
                      (let [url "https://static.zapflow.com/survey/teollisuussijoitus/Tesi_impact_information_reporting_template_companies_2025.xlsx"]
                        (oops/ocall js/window "open" url "_blank")))}
         (loc/tr :survey-import-get-transfer-file)]
        [:div
         {:class "relative bg-primary-regular border border-solid border-secondary-light-border rounded flex flex-row justify-items-center align-items-center font-semibold px-4 gap-2 whitespace-nowrap hover:bg-primary-gloomy"}
         [upload/upload-input
          {:accept     ".xlsx, .xls"
           :class      "absolute w-full h-full opacity-0 block pt-[29px] pl-0 pt-0 pr-0 pointer"
           :input-id   (str "input-id-" (gensym))
           :on-change! (fn [e]
                         (let [target (dg/target (:original-event e))
                               file (oops/oget (:files e) ".0")]
                           (when file
                             (rf/dispatch [::event/validate-uploaded-excel survey-id file]))
                           ;;Without this the event will not run if the same file is selected
                           (oops/oset! target "value" "")))}]
         (loc/tr :survey-import-upload-transfer-file)]]])))

(defn tesi-fund-excel-block
  [survey-id]
  (let [show-excel? @(rf/subscribe [::sub/excel-visible? survey-id])]
    (when show-excel?
      [:div
       {:class "flex items-center flex-auto justify-end gap-2"}
       [:div
        {:class "h-10 pt-1 pr-2 mb-0.5 flex flex-row content-stretch gap-4"}
        [button/secondary-open
         {:on-click (fn []
                      (let [url "https://static.zapflow.com/survey/teollisuussijoitus/Tesi_impact_information_reporting_template_funds_2025.xlsx"]
                        (oops/ocall js/window "open" url "_blank")))}
         [:i.fal.fa-cloud-download]
         (loc/tr :survey-import-get-transfer-file)]
        [:div
         {:class "relative border-0 flex flex-row justify-items-center align-items-center font-semibold px-4 gap-2 whitespace-nowrap hover:text-secondary-light-text-accent"}
         [upload/upload-input
          {:accept     ".xlsx, .xls"
           :class      "absolute w-full h-full opacity-0 block pt-[29px] pl-0 pt-0 pr-0 pointer"
           :input-id   (str "input-id-" (gensym))
           :on-change! (fn [e]
                         (let [target (dg/target (:original-event e))
                               file (oops/oget (:files e) ".0")]
                           (when file
                             (rf/dispatch [::event/validate-uploaded-excel survey-id file]))
                           ;;Without this the event will not run if the same file is selected
                           (oops/oset! target "value" "")))}]
         [:i.fal.fa-cloud-upload]
         (loc/tr :survey-import-upload-transfer-file)]]])))
